import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { ArrowRight, Anchor, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const JURISDICTIONS = [
    { code: "KY", name: "Cayman Islands", currency: "KYD", noTax: true },
    { code: "US", name: "United States", currency: "USD", noTax: false },
    { code: "GB", name: "United Kingdom", currency: "GBP", noTax: false },
    { code: "CA", name: "Canada", currency: "CAD", noTax: false },
    { code: "BS", name: "Bahamas", currency: "BSD", noTax: true },
    { code: "BB", name: "Barbados", currency: "BBD", noTax: false },
    { code: "AU", name: "Australia", currency: "AUD", noTax: false },
    { code: "OTHER", name: "Other", currency: "USD", noTax: false },
];

export default function Onboarding() {
    const navigate = useNavigate();
    const { refreshOrgs, switchOrg } = useAuth();
    const [step, setStep] = useState(1);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        name: "", jurisdiction: "KY", currency: "KYD",
        industry: "", size: "1-10", fiscal_year_start_month: 1,
    });

    const setF = (k, v) => setForm((x) => ({ ...x, [k]: v }));

    const handleJurisdiction = (code) => {
        const j = JURISDICTIONS.find((x) => x.code === code);
        setForm((x) => ({ ...x, jurisdiction: code, currency: j?.currency || x.currency }));
    };

    const submit = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/orgs", form);
            await refreshOrgs();
            await switchOrg(data.org.id);
            toast.success("Business created — welcome aboard!");
            navigate("/app");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const j = JURISDICTIONS.find((x) => x.code === form.jurisdiction);

    return (
        <div className="min-h-screen bg-ocean-mesh">
            <div className="max-w-3xl mx-auto px-6 py-12">
                <Logo />
                <div className="mt-10 rounded-3xl bg-white border border-sand-200 shadow-card p-8 sm:p-12">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-secondary-dark font-semibold">
                        <Anchor className="h-3.5 w-3.5" /> Step {step} of 3
                    </div>
                    <h1 className="mt-3 font-heading text-4xl text-ocean-700">
                        {step === 1 && "Let's set up your business."}
                        {step === 2 && "Where do you do business?"}
                        {step === 3 && "Last touches."}
                    </h1>
                    <p className="mt-2 text-muted-foreground">
                        {step === 1 && "Give your company a name. You can change anything later."}
                        {step === 2 && "Pick your jurisdiction. Cayman gets no-tax mode out of the box."}
                        {step === 3 && "Industry & team size help us tailor your dashboard."}
                    </p>

                    <div className="mt-8 space-y-6">
                        {step === 1 && (
                            <>
                                <div>
                                    <Label>Business name</Label>
                                    <Input value={form.name} onChange={(e) => setF("name", e.target.value)} className="mt-1.5 h-11" placeholder="Acme Holdings Ltd." data-testid="onboarding-name" />
                                </div>
                            </>
                        )}
                        {step === 2 && (
                            <>
                                <div>
                                    <Label>Jurisdiction</Label>
                                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                                        {JURISDICTIONS.map((opt) => (
                                            <button
                                                type="button"
                                                key={opt.code}
                                                onClick={() => handleJurisdiction(opt.code)}
                                                data-testid={`jurisdiction-${opt.code}`}
                                                className={`rounded-xl border p-3 text-left transition-colors ${
                                                    form.jurisdiction === opt.code
                                                        ? "border-secondary bg-secondary/5 text-ocean-700"
                                                        : "border-sand-300 text-muted-foreground hover:border-sand-400"
                                                }`}
                                            >
                                                <div className="text-sm font-medium text-ocean-700">{opt.name}</div>
                                                <div className="text-[11px] text-muted-foreground">{opt.currency}{opt.noTax && " · no-tax"}</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                {j?.noTax && (
                                    <div className="rounded-2xl border border-coral/30 bg-coral/5 p-5 flex gap-3">
                                        <Check className="h-5 w-5 text-coral-dark mt-0.5" />
                                        <div className="text-sm text-ocean-700">
                                            <div className="font-medium">No-tax mode will be enabled</div>
                                            <div className="text-muted-foreground mt-1">
                                                This {j.name} company will be configured as not subject to standard corporate income tax. Invoices and reports default to zero tax. You can always override per line item or change later in Settings.
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <Label>Reporting currency</Label>
                                    <Select value={form.currency} onValueChange={(v) => setF("currency", v)}>
                                        <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {["KYD", "USD", "GBP", "EUR", "CAD", "AUD", "BSD", "BBD"].map((c) => (
                                                <SelectItem key={c} value={c}>{c}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </>
                        )}
                        {step === 3 && (
                            <>
                                <div>
                                    <Label>Industry</Label>
                                    <Input value={form.industry} onChange={(e) => setF("industry", e.target.value)} placeholder="e.g. Hospitality, Software, Marine" className="mt-1.5 h-11" data-testid="onboarding-industry" />
                                </div>
                                <div>
                                    <Label>Team size</Label>
                                    <Select value={form.size} onValueChange={(v) => setF("size", v)}>
                                        <SelectTrigger className="mt-1.5 h-11" data-testid="onboarding-size"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="solo">Just me</SelectItem>
                                            <SelectItem value="1-10">1–10</SelectItem>
                                            <SelectItem value="11-50">11–50</SelectItem>
                                            <SelectItem value="51+">51+</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="mt-10 flex items-center justify-between">
                        <Button variant="ghost" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1}>Back</Button>
                        {step < 3 ? (
                            <Button onClick={() => setStep((s) => s + 1)} disabled={step === 1 && !form.name} className="bg-ocean-700 text-white rounded-xl" data-testid="onboarding-next">
                                Continue <ArrowRight className="h-4 w-4 ml-1.5" />
                            </Button>
                        ) : (
                            <Button onClick={submit} disabled={busy} className="bg-ocean-700 text-white rounded-xl" data-testid="onboarding-finish">
                                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Open my books
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
